import { MapIcon } from '@heroicons/react/24/outline';
import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/solid';
import maplibregl from 'maplibre-gl';
import type { ChangeEvent } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import Skeleton from 'react-loading-skeleton';
import { Provider, useSelector } from 'react-redux';

import type { RootState } from '../../../store/store';
import store from '../../../store/store';
import { getLabelByValue } from '../../../utils/attributions';
import classNames from '../../../utils/classNames';
import { JWTHeader } from '../../../utils/JWTHeader';
import Buildings3D from '../../MapLibre/Buildings3D';
import { MapLibreProvider, useOptionalMapLibre } from '../../MapLibre/MapLibreContext';
import OverzoomTileLayer from '../../MapLibre/OverzoomTileLayer';
import { SunlightProvider } from '../../MapLibre/SunlightContext';
import { SunlightControl } from '../../MapLibre/SunlightControl';
import { showToast } from '../Notification/ToastrNotif.module';
import RGPDPopup from '../Popup';
import Spinner from '../Spinner';

const NB_YEAR_COPRO_SALES = 3;

const salesDict = {
  nbSales: 'Nb',
  nbSalesAvgYear: 'Nb moyen / an',
  salesAmount: 'Montant total (€)',
  salesAmountAvgYear: 'Montant / an (€)',
  salesCommission: 'Commission estimée total (€)',
  salesCommissionAvgYear: 'Commission estimée / an (€)',
};

type MapView = {
  center: [number, number]; // [lat, lng]
  zoom: number;
  pitch: number;
  bearing: number;
};

const DEFAULT_MAIN_VIEW: MapView = {
  center: [46, 2],
  zoom: 8,
  pitch: 0,
  bearing: 0,
};

const DEFAULT_POPUP_VIEW: MapView = {
  center: [46, 2],
  zoom: 8,
  pitch: 60,
  bearing: 0,
};

const captureMapView = (map: maplibregl.Map | null): MapView | null => {
  if (!map) {
    return null;
  }

  const center = map.getCenter();

  return {
    center: [center.lat, center.lng],
    zoom: map.getZoom(),
    pitch: map.getPitch(),
    bearing: map.getBearing(),
  };
};

const applyViewToMap = (map: maplibregl.Map | null, view: MapView) => {
  if (!map) {
    return;
  }

  map.jumpTo({
    center: { lat: view.center[0], lng: view.center[1] },
    zoom: view.zoom,
    pitch: view.pitch,
    bearing: view.bearing,
  });
};

const roundTo = (value: number, precision: number) => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

const normalizeView = (view: MapView): MapView => ({
  center: [roundTo(view.center[0], 6), roundTo(view.center[1], 6)],
  zoom: roundTo(view.zoom, 3),
  pitch: roundTo(view.pitch, 2),
  bearing: roundTo(view.bearing, 2),
});

const viewsEqual = (a: MapView, b: MapView) =>
  a.center[0] === b.center[0]
  && a.center[1] === b.center[1]
  && a.zoom === b.zoom
  && a.pitch === b.pitch
  && a.bearing === b.bearing;

interface CoproLocation {
  id: number;
  lat: number;
  long: number;
  banId?: string;
  [key: string]: unknown;
}

const findLocationById = (locations: CoproLocation[], id: number | null | undefined) =>
  typeof id === 'number' ? locations.find((location) => location.id === id) ?? null : null;

const findLocationByBanId = (locations: CoproLocation[], banId: string | undefined) =>
  banId ? locations.find((location) => location.banId === banId) ?? null : null;

interface BanFeature {
  geometry: { coordinates: [number, number] };
  properties: {
    id: string;
    label: string;
  };
}

function CoproPopupContent({ details, sales, attributions }) {
  const formatNumber = (value: number) => value.toFixed(0).replaceAll(/\d(?=(\d{3})+$)/g, '$& ').replace('.', ',');

  const openGoogleMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="max-w-52 md:max-w-full">
      <h2>
        <b>Nom : </b>
        {details?.name || <Skeleton count={1} width={20} height={20} />}
      </h2>
      <h2>
        <b>Lots : </b>
        {details?.batchSize || <Skeleton count={1} width={20} height={20} />}
      </h2>
      <h2>
        <b>Adresse : </b>
        {details?.addresses[0]?.address
          ? `${details.addresses[0].address} ${details.addresses[0].postalCode} ${details.addresses[0].city}`
          : <Skeleton count={1} width={20} height={20} />}
      </h2>
      {details?.remoteId && (
        <h2>
          <b>Immatriculation : </b>
          {details.remoteId || <Skeleton count={1} width={20} height={20} />}
        </h2>
      )}
      <div className="mt-2 font-semibold">Ventes sur les 3 dernières années :</div>
      {salesDict && Object.entries(salesDict).map(([key, label]) => (
        <div className="ml-2" key={`salesMapDetails${key}`}>
          <h2>
            <b>
              {label}
              {' '}
              :
              {' '}
            </b>
            {sales && sales[key] !== undefined ? formatNumber(sales[key]) : <Skeleton count={1} width={40} height={20} />}
          </h2>
        </div>
      ))}
      {attributions && (
        <>
          <div className="mt-2 font-semibold">Rôles :</div>
          {attributions.length > 0 ? (
            attributions.map((attribution) => (
              <h2 className="ml-2" key={`functionType${attribution.fonctionType}`}>
                <b>
                  {getLabelByValue(attribution.fonctionType)}
                  {' '}
                  :
                  {' '}
                </b>
                {attribution.user.firstname}
                {' '}
                {attribution.user.lastname ? attribution.user.lastname.toUpperCase() : ''}
              </h2>
            ))
          ) : (
            <div className="ml-2">Aucune attribution.</div>
          )}
        </>
      )}

      {details?.addresses[0].address && details?.addresses[0].postalCode && details?.addresses[0].city && (
        <div>
          <button
            onClick={() => openGoogleMaps(`${details?.addresses[0].address} ${details?.addresses[0].postalCode} ${details?.addresses[0].city}`)}
            className={classNames('p-1 rounded bg-rgpd-blue-dark focus:outline-none text-white text-small w-full mt-2')}
            aria-label="Voir sur Google Maps"
          >
            <MapIcon className="mr-2 inline-block h-5 w-5 text-white" />
            {' '}
            Voir sur Google Maps
          </button>
        </div>
      )}

    </div>
  );
}

interface CoproMapsProps {
  coproIds?: number[];
  coproColor?: { [key: number]: string };
  selectedLocationIdProp?: number;
}

function CoproMap({ coproIds = [], coproColor, selectedLocationIdProp }: CoproMapsProps) {
  const [locations, setLocations] = useState<CoproLocation[]>([]);
  const [selectedCoproDetails, setSelectedCoproDetails] = useState(null);
  const [selectedCoproAttribution, setSelectedCoproAttribution] = useState(null);
  const [selectedCoproSales, setSelectedCoproSales] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<BanFeature[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [noCoproMatch, setNoCoproMatch] = useState(false);
  const [mainView, setMainView] = useState<MapView>(DEFAULT_MAIN_VIEW);
  const [popupView, setPopupView] = useState<MapView>(DEFAULT_POPUP_VIEW);
  const mainMapRef = useRef<maplibregl.Map | null>(null);
  const popupMapRef = useRef<maplibregl.Map | null>(null);
  const pendingLocationsRequestRef = useRef<{ companyId: number | string | null; token: number }>({ companyId: null, token: 0 });
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitiallyFitted, setHasInitiallyFitted] = useState(false);
  const [isMainMapReady, setIsMainMapReady] = useState(false);

  const clearSelection = useCallback(() => {
    setSelectedLocationId(null);
    setSelectedCoproDetails(null);
    setSelectedCoproAttribution(null);
    setSelectedCoproSales(null);
  }, []);

  const updateMainView = useCallback((view: MapView) => {
    setMainView((current) => {
      const normalized = normalizeView(view);
      return viewsEqual(current, normalized) ? current : normalized;
    });
  }, []);

  const updatePopupView = useCallback((view: MapView) => {
    setPopupView((current) => {
      const normalized = normalizeView(view);
      return viewsEqual(current, normalized) ? current : normalized;
    });
  }, []);

  const openPopup = useCallback(() => {
    const currentView = captureMapView(mainMapRef.current);
    updatePopupView(currentView ? { ...currentView, pitch: 60 } : DEFAULT_POPUP_VIEW);
    setIsPopupOpen(true);
  }, [updatePopupView]);

  const closePopup = useCallback(() => {
    setIsPopupOpen(false);
    clearSelection();
  }, [clearSelection]);

  const fetchCoproLocations = useCallback(async() => {
    const companyId = company?.id;
    if (!companyId || !jwt) {
      setLocations([]);
      return;
    }

    if (pendingLocationsRequestRef.current.companyId === companyId) {
      return;
    }

    const token = pendingLocationsRequestRef.current.token + 1;
    pendingLocationsRequestRef.current = { companyId, token };

    setIsLoading(true);
    try {
      const response = await fetch(`/api/companies/${companyId}/copros/locations`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error('Failed to fetch copro locations');
      }
      const data = await response.json();
      const rawData: CoproLocation[] = Array.isArray(data) ? data : [];
      const filteredData = rawData.filter((location) => location.lat && location.long);
      if (
        pendingLocationsRequestRef.current.companyId === companyId
        && pendingLocationsRequestRef.current.token === token
      ) {
        setLocations(filteredData);
      }
    } catch (error) {
      if (
        pendingLocationsRequestRef.current.companyId === companyId
        && pendingLocationsRequestRef.current.token === token
      ) {
        console.error('[CoproMap] fetchCoproLocations error', error);
        setLocations([]);
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
      } else {
        console.error('[CoproMap] stale fetchCoproLocations response ignored', error);
      }
    } finally {
      if (
        pendingLocationsRequestRef.current.companyId === companyId
        && pendingLocationsRequestRef.current.token === token
      ) {
        pendingLocationsRequestRef.current = { companyId: null, token };
        setIsLoading(false);
      }
    }
  }, [company?.id, jwt]);

  const fetchCoproData = useCallback(async(coproId: number) => {
    if (!jwt) {
      return;
    }

    const fetchJson = async(url: string) => {
      const response = await fetch(url, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`Failed request: ${url}`);
      }
      return response.json();
    };

    try {
      const [details, attributions, sales] = await Promise.all([
        fetchJson(`/api/copros/${coproId}`),
        fetchJson(`/api/copros/${coproId}/attributions?limit=10000`),
        fetchJson(`/api/copros/${coproId}/stats/sales/${NB_YEAR_COPRO_SALES}`),
      ]);

      setSelectedCoproDetails(details);
      setSelectedCoproAttribution(Array.isArray(attributions?.results) ? attributions.results : []);
      setSelectedCoproSales(sales);
    } catch (error) {
      console.error('[CoproMap] fetchCoproData error', error);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  }, [jwt]);

  const loadCopro = useCallback((coproId: number, locationId?: number | null) => {
    setSelectedLocationId(typeof locationId === 'number' ? locationId : null);
    setSelectedCoproDetails(null);
    setSelectedCoproAttribution(null);
    setSelectedCoproSales(null);
    fetchCoproData(coproId);
  }, [fetchCoproData]);

  const handleSearch = useCallback(async(event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.trim();
    if (query.length <= 2) {
      setSearchResults([]);
      setNoCoproMatch(false);
      return;
    }

    try {
      const response = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5&type=housenumber`);
      if (!response.ok) {
        setSearchResults([]);
        return;
      }
      const data = await response.json().catch(() => null);
      const features: BanFeature[] = Array.isArray(data?.features) ? data.features : [];
      setSearchResults(features);
    } catch (error) {
      setSearchResults([]);
      if ((error as Error).name !== 'TypeError') {
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
      }
    }
  }, []);

  const handleSelectAddress = useCallback((feature: BanFeature) => {
    const [lng, lat] = feature.geometry.coordinates;
    const matchedLocation = findLocationByBanId(locations, feature.properties.id);

    const targetMap = isPopupOpen ? popupMapRef.current : mainMapRef.current;
    const currentBearing = targetMap?.getBearing() ?? 0;
    const targetView: MapView = {
      center: [lat, lng],
      zoom: 16,
      pitch: isPopupOpen ? 60 : 0,
      bearing: currentBearing,
    };

    if (isPopupOpen) {
      updatePopupView(targetView);
    } else {
      updateMainView(targetView);
    }

    if (matchedLocation) {
      loadCopro(matchedLocation.id, matchedLocation.id);
      setNoCoproMatch(false);
    } else {
      setNoCoproMatch(true);
    }

    setSearchResults([]);
  }, [isPopupOpen, locations, loadCopro, updateMainView, updatePopupView]);

  const filteredLocations = useMemo(
    () => locations.filter((location) => coproIds.length === 0 || coproIds.includes(location.id)),
    [locations, coproIds],
  );

  useEffect(() => {
    if (jwt && company) {
      fetchCoproLocations();
    }
  }, [jwt, company, fetchCoproLocations]);

  // If a preselected location is provided, select and center on it when locations are loaded
  useEffect(() => {
    if (!selectedLocationIdProp) {
      return;
    }

    const matched = findLocationById(locations, selectedLocationIdProp);
    if (!matched) {
      return;
    }

    setSelectedLocationId(selectedLocationIdProp);
    loadCopro(selectedLocationIdProp, selectedLocationIdProp);

    const targetView: MapView = {
      center: [matched.lat, matched.long],
      zoom: 16,
      pitch: 0,
      bearing: 0,
    };

    updateMainView(targetView);
    updatePopupView({ ...targetView, pitch: 60 });
  }, [loadCopro, locations, selectedLocationIdProp, updateMainView, updatePopupView]);

  useEffect(() => {
    if (!selectedLocationId || !selectedCoproDetails) {
      return;
    }

    const matchedLocation = findLocationById(locations, selectedLocationId);
    if (!matchedLocation) {
      return;
    }

    const targetView: MapView = {
      center: [matchedLocation.lat, matchedLocation.long],
      zoom: 16,
      pitch: 0,
      bearing: mainView.bearing,
    };

    updateMainView(targetView);
    updatePopupView({ ...targetView, pitch: 60 });
  }, [locations, mainView.bearing, selectedCoproDetails, selectedLocationId, updateMainView, updatePopupView]);

  // Trigger map resize and repaint when popup closes and main map becomes visible again
  useEffect(() => {
    if (!isPopupOpen && mainMapRef.current) {
      try {
        mainMapRef.current.resize();
      } catch (error) {
        console.warn('Error resizing main map:', error);
      }
    }
  }, [isPopupOpen]);

  const fitMainMapToLocations = useCallback((locs: CoproLocation[]) => {
    if (!mainMapRef.current) {
      return;
    }
    if (locs.length === 0) {
      return;
    }

    const filtered = locs.filter((location) => coproIds.length === 0 || coproIds.includes(location.id));
    if (filtered.length === 0) {
      return;
    }

    let minLat = Infinity;
    let minLng = Infinity;
    let maxLat = -Infinity;
    let maxLng = -Infinity;

    filtered.forEach((location) => {
      minLat = Math.min(minLat, location.lat);
      minLng = Math.min(minLng, location.long);
      maxLat = Math.max(maxLat, location.lat);
      maxLng = Math.max(maxLng, location.long);
    });

    if (!Number.isFinite(minLat) || !Number.isFinite(minLng) || !Number.isFinite(maxLat) || !Number.isFinite(maxLng)) {
      return;
    }

    const presetCenter: [number, number] = [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
    const map = mainMapRef.current;
    const targetView: MapView = {
      center: presetCenter,
      zoom: Math.min(map.getZoom(), 8),
      pitch: 0,
      bearing: 0,
    };

    updateMainView(targetView);
    applyViewToMap(map, targetView);

    try {
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 40, duration: 0, pitch: 0, bearing: 0 });
    } catch (error) {
      console.error('[CoproMap] fitBounds error', error);
    }

    try {
      const fittedCenter = map.getCenter();
      const fittedZoom = map.getZoom();
      const fittedView: MapView = {
        center: [fittedCenter.lat, fittedCenter.lng],
        zoom: fittedZoom,
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      };
      updateMainView(fittedView);
    } catch (error) {
      console.warn('[CoproMap] unable to read fitted view', error);
    }
  }, [coproIds, updateMainView]);

  useEffect(() => {
    if (!hasInitiallyFitted && isMainMapReady && mainMapRef.current && locations.length > 0 && !selectedLocationId && !isLoading) {
      fitMainMapToLocations(locations);
      setHasInitiallyFitted(true);
    }
  }, [filteredLocations.length, fitMainMapToLocations, hasInitiallyFitted, isLoading, isMainMapReady, locations, selectedLocationId]);


  function MapLayers({ mapKind }: { mapKind: 'main' | 'popup' }) {
    const map = useOptionalMapLibre();
    const markersRef = useRef<Record<number, maplibregl.Marker>>({});
    const popupRef = useRef<maplibregl.Popup | null>(null);

    useEffect(() => {
      if (mapKind === 'main') {
        if (map) {
          mainMapRef.current = map;
          setIsMainMapReady(true);
        } else {
          mainMapRef.current = null;
          setIsMainMapReady(false);
        }
      } else if (map) {
        popupMapRef.current = map;
      }

      return () => {
        if (mapKind === 'main') {
          if (mainMapRef.current === map) {
            mainMapRef.current = null;
          }
          setIsMainMapReady(false);
        } else if (popupMapRef.current === map) {
          popupMapRef.current = null;
        }
      };
    }, [map, mapKind]);

    useEffect(() => {
      if (!map) {
        return;
      }

      if (mapKind === 'main' && !hasInitiallyFitted && locations.length > 0 && !selectedLocationId && !isLoading) {
          fitMainMapToLocations(locations);
          setHasInitiallyFitted(true);
        }

      if (mapKind === 'main') {
        applyViewToMap(map, mainView);
      } else {
        applyViewToMap(map, popupView);
      }
    }, [map, mapKind, mainView, popupView, filteredLocations.length, fitMainMapToLocations, hasInitiallyFitted, isLoading, locations, selectedLocationId]);

    useEffect(() => {
      if (!map) {
        return;
      }

      const handleViewChange = () => {
        const view = captureMapView(map);
        if (!view) {
          return;
        }

        if (mapKind === 'main') {
          updateMainView(view);
        } else {
          updatePopupView(view);
        }
      };

      const events: Array<'moveend' | 'zoomend' | 'rotateend' | 'pitchend'> = ['moveend', 'zoomend', 'rotateend', 'pitchend'];
      events.forEach((eventName) => {
        map.on(eventName, handleViewChange);
      });

      return () => {
        events.forEach((eventName) => {
          map.off(eventName, handleViewChange);
        });
      };
    }, [map, mapKind, updateMainView, updatePopupView]);

    useEffect(() => {
      if (!map) {
        return;
      }

      Object.values(markersRef.current).forEach((marker) => {
        try {
          marker.remove();
        } catch {
          // ignore
        }
      });
      markersRef.current = {};

      filteredLocations.forEach((location) => {
        try {
          const markerEl = document.createElement('div');
          markerEl.style.width = '28px';
          markerEl.style.height = '28px';
          markerEl.style.borderRadius = '50%';
          markerEl.style.background = coproColor && coproColor[location.id] ? coproColor[location.id] : '#0e7490';
          markerEl.style.boxShadow = '0 0 0 2px #fff inset, 0 1px 4px rgba(0,0,0,0.3)';
          markerEl.style.opacity = '0.85';
          markerEl.style.cursor = 'pointer';
          markerEl.addEventListener('click', () => loadCopro(location.id, location.id));
          const marker = new maplibregl.Marker({ element: markerEl, anchor: 'bottom' })
            .setLngLat([location.long, location.lat])
            .addTo(map);
          markersRef.current[location.id] = marker;
        } catch {
          // ignore marker creation failures
        }
      });

      return () => {
        Object.values(markersRef.current).forEach((marker) => {
          try {
            marker.remove();
          } catch {
            // ignore cleanup failures
          }
        });
        markersRef.current = {};
      };
    }, [map, filteredLocations, coproColor, loadCopro, mapKind]);

    useEffect(() => {
      if (!map) {
        return;
      }

      if (!selectedCoproDetails || !selectedLocationId) {
        if (popupRef.current) {
          try { popupRef.current.remove(); } catch {}
          popupRef.current = null;
        }
        return;
      }

      const marker = markersRef.current[selectedLocationId];
      if (!marker) {
        return;
      }

      try {
        const container = document.createElement('div');
        const root: Root = createRoot(container);
        root.render(
          <Provider store={store}>
            <CoproPopupContent details={selectedCoproDetails} sales={selectedCoproSales} attributions={selectedCoproAttribution} />
          </Provider>,
        );

        if (popupRef.current) {
          popupRef.current.setDOMContent(container).setLngLat(marker.getLngLat());
        } else {
          popupRef.current = new maplibregl.Popup({ closeButton: true, closeOnClick: true, offset: 12 })
            .setDOMContent(container)
            .setLngLat(marker.getLngLat())
            .addTo(map);
          popupRef.current.on('close', clearSelection);
        }
      } catch (error) {
        console.error('[CoproMap] popup update error', error);
      }
    }, [map, selectedCoproDetails, selectedCoproSales, selectedCoproAttribution, selectedLocationId, clearSelection]);

    useEffect(() => () => {
      if (popupRef.current) {
        try { popupRef.current.off('close', clearSelection); } catch {}
        try { popupRef.current.remove(); } catch {}
        popupRef.current = null;
      }
    }, []);

    return null;
  }

  return (
    <div className="relative flex flex-col overflow-hidden rounded-xl" style={{ width: '100%', height: '100%' }}>
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-200/75">
          <Spinner size="xl" />
        </div>
      )}
      {!isLoading && !isPopupOpen && (
        <MapLibreProvider
          center={mainView.center}
          zoom={mainView.zoom}
          className="grow"
          style={{ width: '100%', zIndex: 10, position: 'relative' }}
          jwt={jwt}
          allowPitch={false}
          initialPitch={mainView.pitch}
          initialBearing={mainView.bearing}
        >
          <OverzoomTileLayer isCadastre={false} />
          <MapLayers mapKind="main" />
          <button
            className="text-gray absolute top-0 right-0 m-3 rounded border-2 border-[#00000055] bg-rgpd-white p-1"
            style={{ zIndex: 400 }}
            onClick={openPopup}
            aria-label="Ouvrir la navigation"
          >
            <MagnifyingGlassPlusIcon className="h-6 w-6" />
          </button>
        </MapLibreProvider>
      )}
      {!isLoading && isPopupOpen && (
        <div
          className="flex grow items-center justify-center bg-white"
          style={{ width: '100%', zIndex: 10, position: 'relative' }}
        >
          <div className="text-center font-semibold text-gray-600">Carte élargie en pleine écran</div>
        </div>
      )}

      <div className="flex justify-center">
        <button className="w-full bg-rgpd-blue-dark py-1 text-white" onClick={openPopup}>
          Ouvrir pour naviguer
        </button>
      </div>

      <RGPDPopup isOpen={isPopupOpen} onClose={closePopup} width="80dvw" height="80dvh">
        <div style={{ width: '100%', height: '100%' }}>
          <div className="flex flex-col" style={{ width: '100%', height: '100%' }}>
            <input type="text" placeholder="Rechercher une adresse" onChange={handleSearch} className="w-full border p-2" autoComplete="nope" />
            {Array.isArray(searchResults) && searchResults.length > 0 && (
              <ul className="border">
                {searchResults.map((result, index) => (
                  <li key={index} onClick={() => handleSelectAddress(result)} className="cursor-pointer border-b p-2">
                    {result.properties.label}
                  </li>
                ))}
              </ul>
            )}
            {noCoproMatch && (
              <div className="z-50 w-full bg-red-500 p-2 text-center text-white">Aucune copropriété ne correspond à l'adresse recherchée.</div>
            )}
            <SunlightProvider>
              <MapLibreProvider
                center={popupView.center}
                zoom={popupView.zoom}
                className="grow"
                style={{ width: '100%', height: '100%', position: 'relative' }}
                jwt={jwt}
                initialPitch={popupView.pitch}
                initialBearing={popupView.bearing}
              >
                <OverzoomTileLayer isCadastre={false} />
                {/* 3D buildings only in popup, always enabled based on zoom level */}
                <Buildings3D enabled={true} />
                <MapLayers mapKind="popup" />
                <SunlightControl />
              </MapLibreProvider>
            </SunlightProvider>
          </div>
        </div>
      </RGPDPopup>
    </div>
  );
}

export default CoproMap;
